import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getPayments,
  updatePaymentStatus,
} from "../../services/paymentService";

/* ─── helpers ─── */
const toastCfg = { autoClose: 1000 };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Confirm Modal ─── */
const ConfirmModal = ({ show, actionType, onHide, onSubmit }) => {
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (show) setRejectReason("");
  }, [show]);

  const isApprove = actionType === "approved";

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>{isApprove ? "Approve Payment" : "Reject Payment"}</div>
          <div style={ms.headerSub}>
            {isApprove ? "Confirm approval of this payment" : "Provide a reason for rejection"}
          </div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>

      <div style={ms.body}>
        {!isApprove && (
          <div>
            <label style={ms.label}>Reject Reason <span style={{ color: "#ef4444" }}>*</span></label>
            <input
              style={ms.input}
              placeholder="Enter reason for rejection…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        )}
        {isApprove && (
          <p style={{ color: "#475569", fontSize: "0.87rem", margin: 0 }}>
            Are you sure you want to approve this payment? This action cannot be undone.
          </p>
        )}
      </div>

      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide}>Cancel</button>
        <button
          style={isApprove ? ms.approveBtn : ms.rejectBtn}
          onClick={() => onSubmit(rejectReason)}
        >
          {isApprove
            ? <><Icon icon="solar:check-circle-bold" style={{ fontSize: 15 }} /> Approve</>
            : <><Icon icon="solar:close-circle-bold" style={{ fontSize: 15 }} /> Reject</>
          }
        </button>
      </div>
    </Modal>
  );
};

/* ─── Modal styles ─── */
const ms = {
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9",
  },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub:   { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    background: "#f1f5f9", border: "none", borderRadius: 8,
    width: 32, height: 32, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b",
  },
  body:  { padding: "18px 24px" },
  label: {
    display: "block", fontSize: "0.75rem", fontWeight: 700,
    color: "#475569", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9",
  },
  cancelBtn: {
    padding: "8px 18px", background: "#f1f5f9", border: "none",
    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
    fontWeight: 600, color: "#475569", cursor: "pointer",
  },
  approveBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
  rejectBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#991b1b,#dc2626)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export const PaymentListLayer = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  /* ── fetch ── */
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getPayments();
      if (res?.success) setList(res.data || []);
    } catch {
      if (!silent) toast.error("Failed to load", toastCfg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  /* ── modal open ── */
  const openModal = (id, type) => {
    setSelectedId(id);
    setActionType(type);
    setShowModal(true);
  };

  /* ── submit ── */
  const handleSubmit = async (rejectReason) => {
    try {
      const formData = new FormData();
      formData.append("request_id", String(selectedId));
      formData.append("status", actionType);
      if (actionType === "rejected") {
        if (!rejectReason) {
          toast.error("Reject reason required", toastCfg);
          return;
        }
        formData.append("reject_reason", rejectReason);
      }
      await updatePaymentStatus(formData);
      toast.success(actionType === "approved" ? "Approved" : "Rejected", toastCfg);
      setShowModal(false);
      fetchData();
    } catch {
      toast.error("Failed", toastCfg);
    }
  };

  /* ── client-side status + date filter ── */
  const filteredList = useMemo(() => {
    let d = !statusFilter ? list : list.filter((u) => u.status === statusFilter);
    if (fromDate) d = d.filter((u) => (u.created_at || "").slice(0, 10) >= fromDate);
    if (toDate)   d = d.filter((u) => (u.created_at || "").slice(0, 10) <= toDate);
    return d;
  }, [list, statusFilter, fromDate, toDate]);

  /* ── stats ── */
  const stats = useMemo(() => ({
    total:    list.length,
    pending:  list.filter((u) => u.status === "requested").length,
    approved: list.filter((u) => u.status === "approved").length,
    rejected: list.filter((u) => u.status === "rejected").length,
  }), [list]);

  /* ── columns ── */
  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 44,
      enableSorting: false,
      cell: ({ row }) =>
        row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      accessorKey: "user_uid",
      header: "User",
      cell: ({ getValue }) => <span style={ts.name}>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "mobile_number",
      header: "Mobile",
      cell: ({ getValue }) => <span style={ts.mono}>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "utr_number",
      header: "UTR",
      cell: ({ getValue }) => <span style={ts.mono}>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "price",
      header: "Amount",
      cell: ({ getValue }) => (
        <span style={ts.wallet}>₹{Number(getValue() || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const v = getValue();
        const bg =
          v === "approved"  ? "#dcfce7" :
          v === "rejected"  ? "#fee2e2" : "#fef9c3";
        const fg =
          v === "approved"  ? "#16a34a" :
          v === "rejected"  ? "#dc2626" : "#92400e";
        const label = v === "requested" ? "Pending" : v;
        return (
          <span style={{ ...ts.badge, background: bg, color: fg }}>
            {v === "approved" ? "● " : v === "rejected" ? "○ " : "● "}{label}
          </span>
        );
      },
    },
    {
      accessorKey: "payment_proof_url",
      header: "Proof",
      enableSorting: false,
      cell: ({ getValue }) => (
        <a href={getValue()} target="_blank" rel="noreferrer" style={ts.proofLink}>
          <Icon icon="solar:eye-bold" style={{ fontSize: 13 }} /> View
        </a>
      ),
    },
    {
      id: "actions",
      header: "Action",
      size: 110,
      enableSorting: false,
      cell: ({ row }) => {
        const u = row.original;
        if (u.status !== "requested") {
          return <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>No Action</span>;
        }
        return (
          <div style={{ display: "flex", gap: 6 }}>
            <button style={ts.approveBtn} onClick={() => openModal(u.id, "approved")}>
              <Icon icon="solar:check-circle-bold" style={{ fontSize: 13 }} />
            </button>
            <button style={ts.rejectBtn} onClick={() => openModal(u.id, "rejected")}>
              <Icon icon="solar:close-circle-bold" style={{ fontSize: 13 }} />
            </button>
          </div>
        );
      },
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  /* ── table instance ── */
  const table = useReactTable({
    data: filteredList,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel:       getCoreRowModel(),
    getFilteredRowModel:   getFilteredRowModel(),
    getSortedRowModel:     getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.utr_number?.toLowerCase().includes(v) ||
        row.original.mobile_number?.includes(v) ||
        (row.original.user_uid || "").toLowerCase().includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <div style={s.root}>
      {/* ── Stats row ── */}
      <div style={s.statsRow}>
        {[
          { label: "Total",    value: stats.total,    icon: "solar:card-bold-duotone",          color: "#4B5EAA" },
          { label: "Pending",  value: stats.pending,  icon: "solar:clock-circle-bold-duotone",  color: "#F0A500" },
          { label: "Approved", value: stats.approved, icon: "solar:check-circle-bold-duotone",  color: "#16a34a" },
          { label: "Rejected", value: stats.rejected, icon: "solar:close-circle-bold-duotone",  color: "#dc2626" },
        ].map((c) => (
          <div key={c.label} style={s.statCard}>
            <div style={{ ...s.statIcon, background: `${c.color}14`, color: c.color }}>
              <Icon icon={c.icon} style={{ fontSize: 20 }} />
            </div>
            <div>
              <div style={s.statValue}>{c.value}</div>
              <div style={s.statLabel}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Card ── */}
      <div style={s.card}>
        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search UTR, mobile, user…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="">All Status</option>
              <option value="requested">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <div style={s.dateGroup}>
              <span style={s.dateLabel}>From</span>
              <input type="date" style={s.dateInput} value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }} />
            </div>
            <div style={s.dateGroup}>
              <span style={s.dateLabel}>To</span>
              <input type="date" style={s.dateInput} value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }} />
            </div>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:card-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No payments match your filters
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, filteredList.length)}</strong>
            {" "}of <strong>{filteredList.length}</strong> payments
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}             disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}               disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const pg = Math.max(0, Math.min(pageIndex - 3, pageCount - 7)) + i;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                   disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)}  disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        show={showModal}
        actionType={actionType}
        onHide={() => setShowModal(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

/* ─── Page styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 },
  statCard: {
    background: "#fff", borderRadius: 14, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  statIcon:  { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: "1.4rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  statLabel: { fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 },

  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },

  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },

  searchWrap:  { position: "relative", display: "flex", alignItems: "center" },
  searchIcon:  { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 240, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },

  dateGroup: { display: "flex", alignItems: "center", gap: 5 },
  dateLabel: { fontSize: "0.78rem", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" },
  dateInput: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },

  tableWrap: { overflowX: "auto" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px", background: "#f8fafc", color: "#64748b",
    fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase",
    letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap", textAlign: "left",
  },
  td: {
    padding: "9px 14px", borderBottom: "1px solid #f8fafc",
    color: "#334155", verticalAlign: "middle",
  },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },

  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo:      { fontSize: "0.78rem", color: "#64748b" },
  pgBtns:      { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30, border: "1px solid #e2e8f0",
    background: "#fff", borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

/* ─── Table cell styles ─── */
const ts = {
  name:   { fontWeight: 600, color: "#1e293b", fontSize: "0.83rem" },
  mono:   { fontFamily: "monospace", fontSize: "0.81rem" },
  wallet: { fontWeight: 700, color: "#16632b", fontSize: "0.83rem" },
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap",
  },
  proofLink: {
    display: "inline-flex", alignItems: "center", gap: 4,
    color: "#2563eb", fontSize: "0.78rem", fontWeight: 600, textDecoration: "none",
  },
  approveBtn: {
    display: "inline-flex", alignItems: "center",
    padding: "5px 9px",
    background: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
  },
  rejectBtn: {
    display: "inline-flex", alignItems: "center",
    padding: "5px 9px",
    background: "#fff5f5", color: "#dc2626", border: "1px solid #fecaca",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
  },
};
